import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '../../API/Main/API';
import {
	AnnotationBehaviorDate,
	BehaviorAnnotationProperties,
	DataAnnotationProperties,
	GlobalAnnotationProperties,
	TargetAnnotationProperties,
} from '../../API/ClientAPIHelpers/graphAnnotationProperties';
import { ErrorProperties } from '../../API/identityAPIProperties';
import { AnnotationDateProperties } from '../../API/ClientAPIHelpers/graphProperties';

interface GraphAnnotationStateProperties {
	behaviorAnnotation: DataAnnotationProperties[];
	behaviorAnnotations: AnnotationBehaviorDate[];
	error: ErrorProperties;
	isDeleting: boolean;
	loading: boolean;
	targetAnnotation: DataAnnotationProperties[];
	targetAnnotations: AnnotationDateProperties[];
}

const intialGraphAnnotationState: GraphAnnotationStateProperties = {
	isDeleting: false,
	loading: false,
	targetAnnotations: [],
	behaviorAnnotations: [],
	behaviorAnnotation: [],
	targetAnnotation: [],
	error: {
		status: 0,
		title: '',
	},
};

export const addGlobalAnnotation = createAsyncThunk(
	'/global/annotation/add',
	async (data: GlobalAnnotationProperties, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.global.addGlobalAnnotation(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const addBehaviorAnnotation = createAsyncThunk(
	'/behaviors/annotation/add',
	async (data: BehaviorAnnotationProperties, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.behaviors.addBehaviorAnnotation(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);
export const getBehaviorReductionAllAnnotations = createAsyncThunk(
	'/behaviors/annotations',
	async (id: string, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.behaviors.getAnnotations(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AnnotationBehaviorDate[];
	},
);

export interface DeleteBehaviorAnnotationProperties {
	annotationId: string;
	behaviorId?: string;
}

export const deleteBehaviorAnnotation = createAsyncThunk(
	'/behaviors/delete/annotation/id',
	async (params: DeleteBehaviorAnnotationProperties, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.behaviors.deleteBehaviorAnnotation(params);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as { isSuccessful: boolean };
	},
);
export interface DeleteAnnotationProperties {
	annotationId: string;
	targetId?: string;
}
export const deleteAnnotation = createAsyncThunk(
	'/target/delete/annotation/id',
	async (params: DeleteAnnotationProperties, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.targets.deleteAnnotation(params);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as { isSuccessful: boolean };
	},
);

export const getTargetAllAnnotations = createAsyncThunk(
	'/target/annotations/all',
	async (targetId: string, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.targets.getAnnotations(targetId);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as AnnotationDateProperties[];
	},
);

export const addAnnotation = createAsyncThunk(
	'/targets/annotation/add',
	async (data: TargetAnnotationProperties, thunkAPI) => {
		const response = await API.ClientAPI.GraphAnnotation.targets.addAnnotation(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

const graphAnnotationSlice = createSlice({
	name: 'graphAnnotation',
	initialState: intialGraphAnnotationState,
	reducers: {},
	extraReducers: (builder) => {
		// addGlobalAnnotation;
		builder.addCase(addGlobalAnnotation.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addGlobalAnnotation.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(addGlobalAnnotation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// addBehaviorAnnotation;
		builder.addCase(addBehaviorAnnotation.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addBehaviorAnnotation.fulfilled, (state, action) => {
			state.loading = false;
			state.behaviorAnnotation = action.payload;
		});
		builder.addCase(addBehaviorAnnotation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// getAllAnnotations
		builder.addCase(getBehaviorReductionAllAnnotations.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getBehaviorReductionAllAnnotations.fulfilled, (state, action) => {
			state.loading = false;
			state.behaviorAnnotations = action.payload;
		});
		builder.addCase(getBehaviorReductionAllAnnotations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// deleteBehaviorAnnotation
		builder.addCase(deleteBehaviorAnnotation.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteBehaviorAnnotation.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteBehaviorAnnotation.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});
		// addAnnotation
		builder.addCase(addAnnotation.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(addAnnotation.fulfilled, (state, action) => {
			state.loading = false;
			state.targetAnnotation = action.payload;
		});
		builder.addCase(addAnnotation.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// getAllAnnotations
		builder.addCase(getTargetAllAnnotations.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(getTargetAllAnnotations.fulfilled, (state, action) => {
			state.loading = false;
			state.targetAnnotations = action.payload;
		});
		builder.addCase(getTargetAllAnnotations.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// deleteAnnotation
		builder.addCase(deleteAnnotation.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteAnnotation.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteAnnotation.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});
	},
});

export default graphAnnotationSlice.reducer;
